import { useEffect, useRef } from 'react';

/**
 * One stored rich-text document, rendered.
 *
 * **This is the ONLY surface in the product that renders a stored document as HTML, and it is
 * safe for exactly one reason: the write gate.** Every value it is handed went through
 * `RichText.Sanitize` server-side, which allow-lists the editor's own output and nothing wider
 * — tags, attributes, CSS properties and URL schemes. Sanitising on read as well would be a
 * second, weaker copy of that list, and the two would drift.
 *
 * It began life as `<QualificationDocument>` in the positions feature and moved here on
 * 18 Sep 2026, when the inventory item's description became a document too. A shared component
 * with consumers in two suites belongs in `components/`, and keeping one place that does this
 * is the whole point — **reaching for `dangerouslySetInnerHTML` a second time is the thing this
 * file exists to prevent.**
 *
 * The consequence to keep in view, and it now spans three modules: any path that writes
 * `Positions.Education` / `Training` / `Experience`, `InventoryItems.Description` or
 * `PressReleases.Body` WITHOUT going through its own service — a bulk import, a direct SQL fix, a new endpoint — puts
 * unsanitized markup somewhere this component will execute. There is no such path today, and
 * adding one means sanitising there too. That is not optional.
 *
 * An absent document is an em dash, never an empty box: a description nobody has written and
 * one that renders as blank are different facts, and the dash is what says which.
 */
export function RichTextDocument({
  html,
  imageUrls,
}: {
  html: string | null;
  /**
   * Object URLs for the pictures this document places in its text, keyed by attachment id —
   * `useAttachmentImages`. Only press releases supply it (12 Oct 2026).
   *
   * **The stored markup carries no `src`**, because `RichText.Policy.WithAttachedImages`
   * allows `data-attachment` and nothing that could hold a URL. So the picture is drawn here
   * or not at all, from bytes the caller fetched through the authenticated client — a document
   * that reaches this component some other way renders its text and no pictures, which is the
   * failure direction to want.
   */
  imageUrls?: Record<string, string>;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /*
    Set AFTER the markup is in the DOM, never woven into the string. Building the `src` into
    the HTML would mean concatenating a URL into markup that is about to be handed to
    `dangerouslySetInnerHTML` — the one thing this file exists to keep nobody doing. Here the
    value is assigned to a property on an element the browser already parsed, so it is a value
    and cannot become markup.
  */
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    root.querySelectorAll('img[data-attachment]').forEach((node) => {
      const img = node as HTMLImageElement;
      const url = imageUrls?.[img.getAttribute('data-attachment') ?? ''];

      if (url) {
        img.src = url;
        img.removeAttribute('data-unresolved');
      } else {
        // Not yet fetched, or not an image the server verified. Drawn as an empty frame by
        // `.rich-text`, never as a broken-image glyph.
        img.removeAttribute('src');
        img.setAttribute('data-unresolved', 'true');
      }
    });
  }, [html, imageUrls]);

  if (!html || html.trim().length === 0) {
    return <p className="text-sm text-slate-400">—</p>;
  }

  return (
    // `.rich-text` is the shared document treatment — the same rules the editor renders its
    // own content with, so a document reads the same on the form and on the profile.
    <div
      ref={ref}
      className="rich-text text-sm text-slate-800"
      // eslint-disable-next-line react/no-danger -- sanitized on write; see the note above.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
